// Utilities
import { defineStore } from "pinia";
import defineAbilitiesFor from "../permissoes/index";
import User from "@/models/UserModel";
import { useSessionStorage } from "@vueuse/core";
import { limparCachePreferencias } from "@/composables/tabela/useTabelaPreferencias";
import { menorFimDeDelegacaoAtiva } from "@/shared/autorizacao/compilador";
import type { Delegacao } from "@/shared/autorizacao/compilador";

export default defineStore("user", {
  state: () => ({
    userProperties: null,
    _ultimaRotaVisitada: useSessionStorage("ultimaRota", ""),
    _ability: {},
    /**
     * T114 (DEL-02) — o timer pendente de `agendarExpiracaoDaAbility`, guardado
     * para nunca deixar um `setTimeout` órfão: toda troca de usuário (novo
     * login, logout com `salvarUsuario(null)`) ou reagendamento cancela o
     * anterior antes de criar outro. `null` quando não há nada agendado.
     */
    _timeoutExpiracaoDelegacao: null as ReturnType<typeof setTimeout> | null,
  }),

  actions: {
    defineAbilities() {
      this._ability = defineAbilitiesFor(this.user);
    },
    /**
     * DEL-02 (T114) — agenda `recarregarPermissoes()` para o fim da MENOR
     * delegação ativa que este usuário recebeu
     * (`shared/autorizacao/delegacao.ts`, `menorFimDeDelegacaoAtiva`).
     *
     * **O front NÃO é a autoridade sobre a vigência da delegação.** O backend
     * reavalia a cada request, contra o próprio relógio (B9.1,
     * `docs/contratos-backend.md` §9) — é ele quem nega de verdade depois que
     * a delegação vence. Este agendamento só evita que a TELA continue
     * mostrando, por até `fim - agora` de atraso, um acesso que o servidor já
     * não concede mais; se o `setTimeout` atrasar (aba em segundo plano) ou
     * nunca disparar, nenhuma autorização real foi comprometida — só a UI
     * fica desatualizada até a próxima ação recarregar a página.
     *
     * Sempre limpa o agendamento anterior primeiro (critério 3: nenhum
     * `setTimeout` órfão sobrevive a um reagendamento).
     */
    agendarExpiracaoDaAbility() {
      this.limparAgendamentoDeExpiracao();

      // `this.user` é sempre um `User` (getter abaixo nunca devolve
      // null/undefined). Desde a T158, `delegacoes` É campo do `UserModel`
      // (passthrough de `user.delegacoes` no construtor) e
      // `shared/autorizacao/index.ts:184` o repassa a `compilarConcessao` —
      // os dois pontos que faltavam aqui já estão ligados. O cast continua
      // necessário porque o getter `user` (abaixo) tipa como `any` (AD-025,
      // dívida à parte de `userProperties` não tipado no state), não porque
      // o campo falte no `UserModel`. Em produção esta linha segue inerte
      // até o backend entregar o dado (BE-11, `docs/contratos-backend.md`
      // §9): `user.delegacoes` é `undefined` em todo payload real hoje.
      const usuario = this.user as { email: string; delegacoes?: readonly Delegacao[] };
      const fim = menorFimDeDelegacaoAtiva(usuario, new Date());
      if (!fim) return;

      const atraso = fim.getTime() - Date.now();
      // Já vencida no instante do login — a Ability que acabou de ser
      // montada já saiu SEM essa delegação; não há o que agendar.
      if (atraso <= 0) return;

      this._timeoutExpiracaoDelegacao = setTimeout(() => {
        this.recarregarPermissoes();
      }, atraso);
    },
    /**
     * Refaz a Ability (a delegação que venceu já não entra) e reagenda para a
     * PRÓXIMA a vencer, se houver mais de uma ativa ao mesmo tempo.
     */
    recarregarPermissoes() {
      this.defineAbilities();
      this.agendarExpiracaoDaAbility();
    },
    /** Nenhum `setTimeout` órfão sobrevive à troca de usuário (critério 3 de T114). */
    limparAgendamentoDeExpiracao() {
      if (this._timeoutExpiracaoDelegacao !== null) {
        clearTimeout(this._timeoutExpiracaoDelegacao);
        this._timeoutExpiracaoDelegacao = null;
      }
    },
    /**
     * O `any` fica, com dono. Trocá-lo por `User | null` obriga tipar
     * `userProperties` no state, e aí o Pinia desembrulha a instância de classe:
     * o tipo resultante deixa de casar com `User` em seis pontos
     * (`shared/autorizacao/index.ts` ×3, este arquivo, e dois testes) — medido em
     * T48. O conserto de verdade é não guardar instância de classe no store, que
     * é redesenho do store de usuário, não escopo desta tarefa. Registrado no
     * AD-025.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    salvarUsuario(user: any, expiraEm = 0) {
      // Cache de preferencia de tabela: por ABA e por e-mail
      // (`docs/preferencias-de-tabela.md` §4). O doc manda limpar no LOGOUT, e
      // aqui nao e o logout: `AuthApi.logout()` nao passa por esta acao. O que
      // este gancho garante e mais estreito, e basta para o efeito exigido —
      // "a preferencia de um usuario nao sobrevive na aba para o proximo que
      // logar nela" — porque TODO LOGIN passa por aqui e nenhuma tabela monta
      // antes: `carregar()` le o cache ANTES de qualquer `listar()`.
      //
      // Residuo aceito: entre o logout e o login seguinte a chave fica legivel
      // no `sessionStorage` da aba. O motivo de nao estar no logout (pedagio de
      // 24 warnings em `libs/AuthApi.ts`, territorio da T66) esta na nota de
      // T39 no `tasks.md`. Uma versao anterior deste comentario afirmava que
      // esta acao e "o unico ponto por onde toda sessao entra e sai" — falso, e
      // derrubado pela `docs/arquivo/.specs/features/migracao-feature-based/validation-D3.md`.
      limparCachePreferencias();

      this.userProperties = user;
      localStorage.removeItem("user");
      if (user) {
        localStorage.setItem("expiraEm", JSON.stringify(expiraEm));
        localStorage.setItem("user", JSON.stringify(this.userProperties));
        this.defineAbilities();
        this.agendarExpiracaoDaAbility();
        return this.userProperties;
      }
      // `salvarUsuario(null)` é o logout hoje (`AuthApi.ts:150`) — sem
      // usuário não há delegação para expirar, e um timer da sessão anterior
      // não pode sobreviver a ela (critério 3 de T114).
      this.limparAgendamentoDeExpiracao();
    },
    novaRota(rota: string) {
      this._ultimaRotaVisitada = rota;
      return this._ultimaRotaVisitada;
    },
  },

  getters: {
    user: (state) => {
      if (state.userProperties) {
        return state.userProperties;
      }
      const usuarioLogado = localStorage.getItem("user");
      if (usuarioLogado) return new User(JSON.parse(usuarioLogado));
      else return new User({}); // TODO: CRIAR ESSA FUNÇAÕ DE LOGOUT // fazerLogout()
    },
    /**
     * Se o usuário logado é cliente (T48, PAPEL-06).
     *
     * Deriva de `tipo`, e não de `roles.includes("cliente")`. Dois motivos: `cliente`
     * nunca foi papel — é a natureza do usuário, e por isso `catalogo/legado.ts` a
     * mapeia para `null`; e este getter é REATIVO, enquanto o `export let isCliente`
     * de `shared/autorizacao/index.ts` é uma variável de módulo escrita como efeito
     * colateral de `defineAbilityFor`, que nunca notifica ninguém.
     *
     * `tipo` vem do construtor do `UserModel`: `"cliente"` para usuário do pool
     * Cognito (login por senha) e `"gali"` para federado e Dynamo.
     */
    ehCliente(): boolean {
      return this.user?.tipo === "cliente";
    },
    ability: (state) => {
      return state._ability;
    },
    expiraEm: () => {
      const expiraEm = Number(localStorage.getItem("expiraEm"));
      return expiraEm || 0;
    },
    ultimaRotaVisitada: (state) => {
      return state._ultimaRotaVisitada;
    },
    loggedUser: () => {
      const bruto = localStorage.getItem("user");
      const usuarioLogado = bruto ? new User(JSON.parse(bruto)) : null;

      const { name, email, username } = usuarioLogado
        ? usuarioLogado
        : {
            name: "",
            email: "",
            username: "",
          };
      return {
        displayName: name,
        email,
        id: username,
      };
    },
  },
});
